import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { usePermissionHelpers } from '../lib/auth/helpers';
import { usePermissions } from '../lib/permissions';

interface HeaderProps {}

export const Header: React.FC<HeaderProps> = () => {
  const { user, logout } = useAuthContext();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false); // Mobile menu state
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null); // Add mobile menu ref

  // Use the modern permission system
  const permissions = usePermissions();
  
  // Use the legacy permission helpers for some compatibility
  const {
    canAccessPowerBI,
    canViewCalendar,
    getUserGroup,
    currentUser,
    userGroups,
    loading,
    setCurrentUser
  } = usePermissionHelpers();

  // Check permissions
  const hasAdminAccess = !loading && permissions.hasAdminAccess();
  const hasPowerBIAccess = !loading && canAccessPowerBI();
  const hasCalendarAccess = !loading && canViewCalendar();
  const has2dVisualizationAccess = !loading && permissions.ui.navigation.show2DVisualization;
  const has3dVisualizationAccess = !loading && permissions.ui.navigation.show3DVisualization;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
      // Also handle mobile menu click outside
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Set current user in permission store when user is available
  useEffect(() => {
    if (user?.username && !currentUser) {
      setCurrentUser(user.username);
    }
  }, [user, currentUser, setCurrentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setShowProfileMenu(false);
    setShowMobileMenu(false); // Close mobile menu on navigation
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const getDisplayName = (user: any): string => {
    if (!user) return 'User';
    
    if (typeof window !== 'undefined' && (window as any).msalInstance) {
      try {
        const accounts = (window as any).msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          if (account.name) {
            return account.name;
          } else if (account.username) {
            if (account.username.includes('@')) {
              return account.username.split('@')[0];
            }
            return account.username;
          }
        }
      } catch (error) {
        // Silently continue to fallbacks if MSAL access fails
      }
    }
    
    if (user.fullName) {
      return user.fullName;
    }
    if (user.name) {
      return user.name;
    }
    if (user.username) {
      if (user.username.includes('@')) {
        return user.username.split('@')[0];
      }
      return user.username;
    }
    
    return 'User';
  };

  const getFirstName = (displayName: string): string => {
    if (!displayName || displayName === 'User') return 'User';
    
    const firstName = displayName.split(' ')[0];
    return firstName;
  };

  const userGroup = getUserGroup();

  if (loading) {
    return (
      <header className="app-header">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center space-x-3">
            <div className="logo">CMA</div>
            <div>
              <h1 className="text-xl font-bold">NEC Checkpoint Management</h1>
              <p className="text-sm text-gray-400 hidden sm:block">Passenger Flow & Wait Times</p>
            </div>
          </div>
          <div className="text-gray-400">Loading permissions...</div>
        </div>
      </header>
    );
  }

  const hasPermissionsLoaded = permissions && Object.keys(permissions).length > 0;
  const shouldShowLoading = !hasPermissionsLoaded && !userGroup && user && !loading;
  
  if (shouldShowLoading) {
    if (userGroups && userGroups.length > 0 && !currentUser && user?.username) {
      setCurrentUser(user.username);
    }
    
    return (
      <header className="app-header">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center space-x-3">
            <div className="logo">CMA</div>
            <div>
              <h1 className="text-xl font-bold">NEC Checkpoint Management</h1>
              <p className="text-sm text-gray-400 hidden sm:block">Passenger Flow & Wait Times</p>
            </div>
          </div>
          <div className="text-gray-400">Loading user group...</div>
        </div>
      </header>
    );
  }

  // Navigation items array for reusability
  const navigationItems = [
    { path: '/dashboard', label: 'Dashboard', show: true },
    { path: '/admin', label: 'Admin', show: hasAdminAccess },
    { path: '/powerbi', label: 'Reports', show: hasPowerBIAccess },
    { path: '/visualization2', label: '2D View (Beta)', show: has2dVisualizationAccess },
    { path: '/visualization3d', label: '3D View (Beta)', show: has3dVisualizationAccess },
    { path: '/calendar-panel', label: 'Calendar', show: hasCalendarAccess },
  ].filter(item => item.show);

  return (
    <header className="app-header">
      <div className="flex justify-between items-center w-full">
        <div className="flex items-center space-x-3">
          <div className="logo">CMA</div>
          <div>
            <h1 className="text-xl font-bold">NEC Checkpoint Management</h1>
            <p className="text-sm text-gray-400 hidden sm:block">Passenger Flow & Wait Times</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Desktop Navigation - Hidden on mobile */}
          <nav className="hidden lg:flex items-center space-x-4">
            {navigationItems.map(item => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* User Profile - Always visible */}
          {user && (
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={handleProfileClick}
                className="flex items-center space-x-2 text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-semibold">
                    {getFirstName(getDisplayName(user)).charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="hidden sm:inline">{getFirstName(getDisplayName(user))}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                  <div className="px-4 py-2 text-sm text-gray-700 border-b">
                    <div className="font-medium">{getDisplayName(user)}</div>
                    {userGroup && (
                      <div className="text-xs text-blue-600 mt-1">
                        {userGroup.displayName || userGroup.groupName}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Mobile Menu Button - Visible only on mobile */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden text-gray-300 hover:text-white p-2 rounded-md"
            aria-label="Menu"
          >
            {showMobileMenu ? (
              // X icon when menu is open
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              // Hamburger icon when menu is closed
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {showMobileMenu && (
        <div 
          ref={mobileMenuRef}
          className="lg:hidden absolute top-16 left-0 right-0 bg-gray-800 shadow-lg z-40 border-t border-gray-700"
        >
          <nav className="flex flex-col">
            {navigationItems.map(item => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className="text-gray-300 hover:text-white hover:bg-gray-700 px-4 py-3 text-left text-sm font-medium transition-colors border-b border-gray-700"
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};